import { useRef, useState } from 'react';
import { Button, tokens } from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  Checkmark24Regular,
  ChevronLeft24Regular,
  ChevronRight24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useMobileOtherInfo } from './MobileOtherInfoContext';
import { useOtherInfoContext } from '../OtherInfoContext';
import { useIsMobile } from '../../hooks/useIsMobile';

export const BottomNavBar = () => {
  const { t } = useTranslation();
  const {
    currentTabIndex,
    totalTabs,
    completionPercentage,
    goNext,
    goPrevious,
    showNavigation,
    setShowNavigation,
  } = useMobileOtherInfo();
  const { params } = useOtherInfoContext();
  const isMobile = useIsMobile();
  const contained = Boolean(params.contained);
  const isLast = currentTabIndex >= totalTabs - 1;
  const canFinish = Boolean(params.persistForm) && !params.readOnly;
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const run = async (action: () => void | Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await action();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleFinish = () =>
    run(async () => {
      const saved = await params.persistForm?.({ skipIfClean: false });
      if (saved === false) return;
      await params.onFormFinish?.();
    });

  return (
    <div
      style={{
        boxSizing: 'border-box',
        position: contained ? 'relative' : 'fixed',
        bottom: contained ? 'auto' : 'var(--app-bottom-nav-height, 0px)',
        left: contained
          ? undefined
          : isMobile
          ? 0
          : 'var(--app-sidebar-width, 0px)',
        right: contained ? undefined : 0,
        width: contained ? '100%' : undefined,
        maxWidth: contained ? '100%' : undefined,
        minWidth: 0,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: showNavigation ? 'center' : 'space-between',
        gap: 8,
        padding: contained ? '8px 0' : '8px 12px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
        zIndex: 100,
      }}
    >
      {showNavigation ? (
        <Button
          appearance="primary"
          icon={<ArrowLeft24Regular />}
          onClick={() => setShowNavigation(false)}
        >
          {t('backToEdit', 'Back to Edit')}
        </Button>
      ) : (
        <>
          <Button
            appearance="subtle"
            icon={<ChevronLeft24Regular />}
            disabled={busy || currentTabIndex <= 0}
            onClick={() => {
              void run(goPrevious);
            }}
            style={{ minWidth: 0, flexShrink: 1 }}
          >
            {t('previous', 'Previous')}
          </Button>

          <Button
            appearance="outline"
            onClick={() => setShowNavigation(true)}
            style={{ minWidth: 0, flexShrink: 1 }}
          >
            {currentTabIndex + 1}/{totalTabs} ({completionPercentage}%)
          </Button>

          {isLast && canFinish ? (
            <Button
              appearance="primary"
              icon={<Checkmark24Regular />}
              iconPosition="after"
              disabled={busy}
              onClick={() => {
                void handleFinish();
              }}
              style={{ minWidth: 0, flexShrink: 1 }}
            >
              {t('finish', 'Finish')}
            </Button>
          ) : (
            <Button
              appearance="primary"
              icon={<ChevronRight24Regular />}
              iconPosition="after"
              disabled={busy || isLast}
              onClick={() => {
                void run(goNext);
              }}
              style={{ minWidth: 0, flexShrink: 1 }}
            >
              {t('nextSection', 'Next Section')}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
